import { drawWagerStartFlash } from "./wagerFlashOverlay";
import { COMPOSITE_FONT_FAMILY } from "./compositeFonts";

export const COMPOSITE_WIDTH = 1280;
export const COMPOSITE_HEIGHT = 720;

/** Chrono canvas uniquement — position fixe 1280×720 (colonne header 11.5rem, pas le DOM live). */
export const COMPOSITE_TIMER_LAYOUT = {
  x: COMPOSITE_WIDTH - 20 - 184,
  y: 6,
  w: 184,
  h: 52,
};

/** Question peinte sur le canvas (comme le chrono) — fiable dans la vidéo composite. */
export const COMPOSITE_QUESTION_LAYOUT = {
  numX: 175,
  numW: 54,
  y: 6,
  h: 52,
};

const COMPOSITE_Q_AFTER_BADGE_GAP = 8;
const COMPOSITE_Q_BEFORE_TIMER_GAP = 10;

/** Cartouche question : bord droit du badge Q → bord gauche du chrono. */
export function compositeQuestionCardRect() {
  const { numX, numW, y, h } = COMPOSITE_QUESTION_LAYOUT;
  const cardX = numX + numW + COMPOSITE_Q_AFTER_BADGE_GAP;
  const cardW = COMPOSITE_TIMER_LAYOUT.x - COMPOSITE_Q_BEFORE_TIMER_GAP - cardX;
  return { cardX, cardW: Math.max(120, cardW), y, h };
}
/** Fond jeu (tailwind md-navy) — zones transparentes de la capture DOM */
export const COMPOSITE_BG_NAVY = "#061224";
/**
 * Opacité du calque plateau (comme l’ancien html-to-image).
 * Appliqué d’abord en voile navy plein écran, puis sur banque / trappes / totaux.
 */
export const COMPOSITE_UI_OPACITY = 0.55;

/** Voile navy plein cadre — le « verre bleuté » sur toute la vidéo (ex-fond capture DOM). */
export function drawCompositeGlassWash(ctx, w, h, alpha = COMPOSITE_UI_OPACITY) {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = COMPOSITE_BG_NAVY;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

/** Layout trappes calé sur play.css (.play-screen--capture) — 1280×720. */
const TRAP_LAYOUT = {
  doorW: 200,
  doorH: 200,
  gap: 10,
  bottomPad: 22,
  letterR: 18,
  letterGap: 10,
};

const LETTERS = ["A", "B", "C", "D"];

function formatMoneyCanvas(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "0 €";
  return new Intl.NumberFormat("fr-FR").format(num) + " €";
}

/** Banque peinte sur le canvas (fiable, comme la question). */
export function drawCompositeBank(ctx, w, h, { bank, show = true } = {}) {
  if (!show || bank == null) return;
  const x = 20;
  const y = 10;
  ctx.save();
  ctx.globalAlpha = COMPOSITE_UI_OPACITY;
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = `600 11px ${COMPOSITE_FONT_FAMILY}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("BANQUE ACTUELLE", x, y);
  ctx.shadowColor = "rgba(255,255,255,0.45)";
  ctx.shadowBlur = 10;
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold 28px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText(formatMoneyCanvas(bank), x, y + 16);
  ctx.restore();
}

/**
 * Trappes sans fond navy (le voile plein écran suffit) :
 * badge + bordure cyan + réponse centrée + mise en bas.
 */
export function drawCompositeTraps(ctx, w, h, live = {}) {
  const answers = live.answers || [];
  const visible = Math.min(
    Number(live.visibleAnswerCount || 0),
    answers.length || 4,
    4,
  );
  if (visible <= 0) return;

  const wagers = live.wagers || [];
  const revealed = new Set(live.revealedDoors || []);
  const correctIndex = live.correctIndex ?? -1;
  const correctHighlighted = Boolean(live.correctHighlighted);
  const phase = live.phase || "";

  const { doorW, doorH, gap, bottomPad, letterR, letterGap } = TRAP_LAYOUT;
  const totalW = doorW * 4 + gap * 3;
  const startX = (w - totalW) / 2;
  const doorY = h - bottomPad - doorH;
  const radius = 22;
  const wagerReserve = 44;

  for (let i = 0; i < visible; i += 1) {
    const x = startX + i * (doorW + gap);
    const isDropped = phase === "dropping" && revealed.has(i);
    const isWinner = phase === "dropping" && correctHighlighted && i === correctIndex;
    const dim = isDropped && !isWinner;
    const doorAlpha = dim ? COMPOSITE_UI_OPACITY * 0.28 : COMPOSITE_UI_OPACITY;

    const bx = x + doorW / 2;
    const by = doorY - letterGap - letterR;
    const wager = Number(wagers[i] || 0);

    // Badge lettre
    ctx.save();
    ctx.globalAlpha = doorAlpha;
    ctx.beginPath();
    ctx.arc(bx, by, letterR, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(79,195,247,0.18)";
    ctx.fill();
    ctx.strokeStyle = "rgba(79,195,247,0.55)";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = "#4FC3F7";
    ctx.font = `bold 18px ${COMPOSITE_FONT_FAMILY}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(LETTERS[i] || String(i + 1), bx, by);
    ctx.restore();

    // Cadre uniquement — pas de fond bleu (voile global déjà appliqué)
    ctx.save();
    ctx.globalAlpha = doorAlpha;
    ctx.beginPath();
    ctx.roundRect(x, doorY, doorW, doorH, radius);
    ctx.shadowColor = "rgba(79,195,247,0.45)";
    ctx.shadowBlur = 28;
    ctx.lineWidth = 4;
    ctx.strokeStyle = isWinner ? "rgba(255,255,255,0.95)" : "rgba(79,195,247,0.85)";
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.globalAlpha = doorAlpha;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const answer = String(answers[i] || "");
    if (answer) {
      ctx.fillStyle = "#ffffff";
      ctx.font = `bold 16px ${COMPOSITE_FONT_FAMILY}`;
      const lines = wrapCanvasLines(ctx, answer, doorW - 28).slice(0, 5);
      const lineH = 20;
      const blockH = lines.length * lineH;
      // Zone réponse = hauteur porte moins la rangée mise (comme TrapDoor grid)
      const contentBottom = doorY + doorH - (wager > 0 ? wagerReserve : 16);
      const contentTop = doorY + 12;
      const midY = contentTop + (contentBottom - contentTop) / 2;
      let ty = midY - blockH / 2 + lineH / 2;
      for (const line of lines) {
        ctx.fillText(line, bx, ty);
        ty += lineH;
      }
    }

    if (wager > 0) {
      // Pas de glow blanc sur trappe fermée : le blur restait trop visible malgré l’opacité réduite.
      if (!dim) {
        ctx.shadowColor = "rgba(255,255,255,0.55)";
        ctx.shadowBlur = 12;
      }
      ctx.fillStyle = "#ffffff";
      ctx.font = `bold 20px ${COMPOSITE_FONT_FAMILY}`;
      ctx.fillText(formatMoneyCanvas(wager), bx, doorY + doorH - 28);
      ctx.shadowBlur = 0;
    }
    ctx.restore();
  }
}

/** Totaux calés comme le CSS capture (à gauche de A / droite de D). */
export function drawCompositeTotals(ctx, w, h, live = {}) {
  if (!live.showTotals) return;
  const { doorW, gap, bottomPad, doorH } = TRAP_LAYOUT;
  const totalW = doorW * 4 + gap * 3;
  const half = totalW / 2;
  const mid = w / 2;
  const baseline = h - bottomPad - 8;

  ctx.save();
  ctx.globalAlpha = COMPOSITE_UI_OPACITY;
  ctx.textBaseline = "bottom";

  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = `600 11px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText("TOTAL MISÉ", mid - half - 12, baseline - 28);
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold 22px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText(formatMoneyCanvas(live.totalWager), mid - half - 12, baseline);

  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = `600 11px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText("RESTANT", mid + half + 12, baseline - 28);
  ctx.fillStyle = "#4FC3F7";
  ctx.font = `bold 22px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText(formatMoneyCanvas(live.remaining), mid + half + 12, baseline);

  ctx.restore();
}

export function applyCanvasDrawQuality(ctx) {
  if (!ctx) return;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
}

export function createCompositeCanvas() {
  const canvas = document.createElement("canvas");
  canvas.width = COMPOSITE_WIDTH;
  canvas.height = COMPOSITE_HEIGHT;
  const ctx = canvas.getContext("2d", { alpha: false });
  applyCanvasDrawQuality(ctx);
  return { canvas, ctx };
}

export function drawCover(ctx, video, w, h, { mirror = false } = {}) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return;

  const scale = Math.max(w / vw, h / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  const dx = (w - dw) / 2;
  const dy = (h - dh) / 2;

  ctx.save();
  if (mirror) {
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, w - dx - dw, dy, dw, dh);
  } else {
    ctx.drawImage(video, dx, dy, dw, dh);
  }
  ctx.restore();
}

/** Calque UI déjà normalisé 1280×720 par capturePlayScreenDom. */
export function uiDrawRect(bitmap, w, h) {
  return { dx: 0, dy: 0, dw: w, dh: h, sx: 0, sy: 0, sw: bitmap.width, sh: bitmap.height };
}

export function drawUiBitmap(ctx, bitmap, rect) {
  const { dx, dy, dw, dh, sx, sy, sw, sh } = rect;
  ctx.drawImage(bitmap, sx, sy, sw, sh, dx, dy, dw, dh);
}

/** Calque plateau par-dessus la webcam (évite multiply qui efface question / totaux clairs). */
export function drawUiLayerMultiply(ctx, bitmap, webcamVideo, w, h, alpha = COMPOSITE_UI_OPACITY) {
  if (!bitmap?.width) return;

  const rect = uiDrawRect(bitmap, w, h);

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = "source-over";
  drawUiBitmap(ctx, bitmap, rect);
  ctx.restore();
}

/** Chrono peint sur le canvas (fluide) — jamais dans la capture DOM. */
export function drawCompositeTimer(ctx, w, h, secondsLeft) {
  const { x: tx, y: ty, w: boxW, h: boxH } = COMPOSITE_TIMER_LAYOUT;

  ctx.save();
  ctx.beginPath();
  ctx.roundRect(tx, ty, boxW, boxH, 10);
  const grad = ctx.createLinearGradient(tx, ty, tx, ty + boxH);
  grad.addColorStop(0, "rgba(239,68,68,0.92)");
  grad.addColorStop(1, "rgba(155,28,28,0.98)");
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.7)";
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.fillStyle = "#fff";
  ctx.font = `bold ${Math.round(h * 0.045)}px ${COMPOSITE_FONT_FAMILY}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(secondsLeft).padStart(2, "0"), tx + boxW / 2 - 12, ty + boxH / 2);
  ctx.font = `bold ${Math.round(h * 0.022)}px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.fillText("sec.", tx + boxW / 2 + 38, ty + boxH / 2);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.restore();
}

function wrapCanvasLines(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let line = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const next = `${line} ${words[i]}`;
    if (ctx.measureText(next).width <= maxWidth) {
      line = next;
    } else {
      lines.push(line);
      line = words[i];
    }
  }
  lines.push(line);
  return lines;
}

/** Question peinte sur le canvas — jamais retirée de la vidéo composite. */
export function drawCompositeQuestion(ctx, w, h, { questionText, questionIndex = 0, questionCount = 0, show = false } = {}) {
  if (!show || !questionText) return;

  const { numX, numW, y, h: boxH } = COMPOSITE_QUESTION_LAYOUT;
  const { cardX, cardW } = compositeQuestionCardRect();

  ctx.save();

  ctx.beginPath();
  ctx.roundRect(numX, y, numW, boxH, 10);
  ctx.fillStyle = "#22d3ee";
  ctx.fill();
  ctx.fillStyle = "#0b1120";
  ctx.font = `bold ${Math.round(h * 0.022)}px ${COMPOSITE_FONT_FAMILY}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const qLabel = questionCount > 0 ? `Q${questionIndex + 1} / ${questionCount}` : `Q${questionIndex + 1}`;
  ctx.fillText(qLabel, numX + numW / 2, y + boxH / 2);

  ctx.beginPath();
  ctx.roundRect(cardX, y, cardW, boxH, 12);
  const grad = ctx.createLinearGradient(cardX, y, cardX, y + boxH);
  grad.addColorStop(0, "#f5f7fb");
  grad.addColorStop(1, "#d8dde8");
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.4)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = "#0b1120";
  const fontSize = Math.round(h * 0.024);
  ctx.font = `bold ${fontSize}px ${COMPOSITE_FONT_FAMILY}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const lines = wrapCanvasLines(ctx, questionText, cardW - 24);
  const lineHeight = fontSize * 1.2;
  const totalH = lines.length * lineHeight;
  let ty = y + boxH / 2 - totalH / 2 + lineHeight / 2;
  for (const line of lines.slice(0, 3)) {
    ctx.fillText(line, cardX + cardW / 2, ty);
    ty += lineHeight;
  }

  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.restore();
}

/** Overlay « Nouvelle chance » / « Dernière chance » — peint sur le canvas composite. */
export function drawCompositeChanceNotice(ctx, w, h, chanceNotice) {
  if (!chanceNotice) return;

  const isLast = chanceNotice === "last_chance";

  ctx.save();
  ctx.fillStyle = "rgba(6, 18, 36, 0.8)";
  ctx.fillRect(0, 0, w, h);

  const cx = w / 2;
  let y = h * 0.28;

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = `600 ${Math.round(h * 0.02)}px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText(isLast ? "Attention" : "Pas de panique", cx, y);
  y += h * 0.1;

  ctx.font = `900 ${Math.round(h * 0.1)}px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillStyle = isLast ? "#f87171" : "#22d3ee";
  ctx.fillText(isLast ? "DERNIÈRE CHANCE" : "NOUVELLE CHANCE", cx, y);
  y += h * 0.12;

  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.font = `500 ${Math.round(h * 0.032)}px ${COMPOSITE_FONT_FAMILY}`;
  const body = isLast
    ? "On vous remet 100 000 € pour la dernière question. Tout se joue ici."
    : "On vous remet 100 000 € pour la question suivante. Profitez-en.";
  for (const line of wrapCanvasLines(ctx, body, w * 0.72)) {
    ctx.fillText(line, cx, y);
    y += Math.round(h * 0.045);
  }
  y += h * 0.035;

  ctx.fillStyle = "#ffffff";
  ctx.font = `bold ${Math.round(h * 0.04)}px ${COMPOSITE_FONT_FAMILY}`;
  ctx.fillText("100 000 €", cx, y);

  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.restore();
}

export function paintCompositeFrame(ctx, { uiBitmap: _uiBitmap, webcamVideo, flashWagerRef, liveOverlayRef }) {
  const w = COMPOSITE_WIDTH;
  const h = COMPOSITE_HEIGHT;
  const live = liveOverlayRef?.current;

  applyCanvasDrawQuality(ctx);
  ctx.clearRect(0, 0, w, h);

  if (webcamVideo?.readyState >= 2) {
    drawCover(ctx, webcamVideo, w, h, { mirror: true });
  } else {
    ctx.fillStyle = COMPOSITE_BG_NAVY;
    ctx.fillRect(0, 0, w, h);
  }

  // Voile bleuté plein écran (ex-fond navy du calque DOM @ 55 %), puis UI plateau.
  // Sans ce wash, les trappes flottent sur la webcam brute et paraissent « transparentes ».
  if (live?.phase !== "chance-notice") {
    drawCompositeGlassWash(ctx, w, h);
    drawCompositeBank(ctx, w, h, { bank: live?.bank, show: live?.bank != null });
    drawCompositeTraps(ctx, w, h, live);
    drawCompositeTotals(ctx, w, h, live);
  }

  if (live?.showQuestion && live?.questionText) {
    drawCompositeQuestion(ctx, w, h, {
      questionText: live.questionText,
      questionIndex: live.questionIndex ?? 0,
      questionCount: live.questionCount ?? 0,
      show: true,
    });
  }

  if (live?.wagerTimerShown && live.wagerSecondsLeft != null) {
    let alpha = 1;
    if (live.wagerTimerFading && live.wagerTimerFadeStartedAt) {
      alpha = Math.max(0, 1 - (Date.now() - live.wagerTimerFadeStartedAt) / 2000);
    }
    if (alpha > 0.01) {
      ctx.save();
      ctx.globalAlpha = alpha;
      drawCompositeTimer(ctx, w, h, live.wagerSecondsLeft);
      ctx.restore();
    }
  }

  if (live?.phase === "chance-notice" && live?.chanceNotice) {
    drawCompositeChanceNotice(ctx, w, h, live.chanceNotice);
  }

  drawWagerStartFlash(ctx, w, h, flashWagerRef);
}
